import React, { useContext, useEffect } from 'react'
import SilageStepLayout from '../SilageStepLayout'
import { HarvestingWrapper, StyledGroup } from '../GrassSilageStep/style';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox, Form } from 'semantic-ui-react';
import { StyledDivider } from '../ElevenStep/style';
import { FormFields } from '~/components/FormFields';
import { ClimaOptionsContext } from '.';
import { CORN_SILAGE_ID } from '../..';
import HarvestSlider from '../GrassSilageStep/HarvestSlider';

const FirstCornStep: React.FC = () => {
  const { control, setValue, clearErrors } = useFormContext()
  const { grainContentTypes, maturityLevels, silageTypes, fieldSizes } = useContext(ClimaOptionsContext)
  const harvestingOptions = silageTypes?.find(silageType => silageType.id === CORN_SILAGE_ID)?.harvestingMethods || []

  const watchHarvesting = useWatch({ control, name: 'maizeSilage.harvestingMethods' }) || []
  const watchMaturity = useWatch({ control, name: 'maizeSilage.maturityLevelId' })
  const watchGrainContent = useWatch({ control, name: 'maizeSilage.grainContent' })
  const watchStroke = useWatch({ control, name: 'maizeSilage.averageFieldSize' })

  const watchHarvestingMethod = useWatch({ control, name: 'maizeSilage.harvestingMethods[0]' })
  const watchMaturityLevel = useWatch({ control, name: 'maizeSilage.maturityLevelId' })

  const isMaturity = Boolean(watchHarvestingMethod?.hasMaturityLevelOptions)
  const isGrain = Boolean(maturityLevels.find(maturityLevel => maturityLevel.id === watchMaturityLevel)?.hasGrainContentOptions)

  useEffect(() => {
    if (watchHarvesting && !Boolean(watchHarvestingMethod?.hasMaturityLevelOptions)) {
      setValue('maizeSilage.grainContent', null)
      clearErrors('maizeSilage.grainContent')
    }
  }, [watchHarvestingMethod])

  useEffect(() => {
    if (!isGrain) {
      setValue('maizeSilage.grainContent', null)
      clearErrors('maizeSilage.grainContent')
    }
  }, [isGrain])

  useEffect(() => {
    if (!isMaturity) {
      setValue('maizeSilage.maturityLevelId', null)
      clearErrors('maizeSilage.maturityLevelId')
    }
  }, [isMaturity])

  function handleChecked(harvestingOption) {
    return [...watchHarvesting, { ...harvestingOption, percent: watchHarvesting.length === 0 ? '100' : 0 }]
  }

  function handleUnchecked(harvestingOption, indexChecked) {
    //deselection of secundary harvest
    if (watchHarvesting?.length > 0 && indexChecked > 0) {
      const tempWatchHarvest = [...watchHarvesting]
      tempWatchHarvest[0].percent = 100
      return tempWatchHarvest.filter(harvest => harvest.id !== harvestingOption.id)
    }

    //deselection of primary harvest
    return []
  }

  return <SilageStepLayout
    infoText={`Die Abfrage zum Futterbauverfahren Silomais bezieht sich auf eine durchschnittliche Anbaufläche Silomais Ihres Betriebs. 
      Bitte berücksichtigen Sie dies bei der Eingabe Ihrer Daten. Falls sich Bewirtschaftung oder Erträge auf mehreren Flächen unterscheiden, 
      bilden Sie geeignete Mittelwerte, um eine repräsentative Angabe zu ermöglichen.`}
  >
    <StyledGroup widths={'equal'}>
      <Controller
        control={control}
        name='maizeSilage.harvestingMethods'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <HarvestingWrapper error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Ernteverfahren'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {harvestingOptions.map(harvestingOption => {

                const indexChecked = watchHarvesting.findIndex(harvesting => harvesting.id === harvestingOption.id)
                const harvestChecked = indexChecked >= 0
                const singleHarvestSelected = watchHarvesting.length === 1
                const maxHarvestSelected = watchHarvesting.length === 2
                const displayProcent = maxHarvestSelected && harvestChecked
                const displaySliderLabels = maxHarvestSelected && harvestChecked && watchHarvesting[1].id === harvestingOption.id

                return <Form.Field>
                  <Checkbox
                    key={harvestingOption.id}
                    style={{ marginBottom: 3 }}
                    label={displayProcent ? `${harvestingOption.label} (${watchHarvesting[indexChecked]?.percent}%)` : harvestingOption.label}
                    className={singleHarvestSelected && !harvestChecked ? 'disable-harvest-method' : ''}
                    disabled={maxHarvestSelected && !harvestChecked}
                    checked={harvestChecked}
                    onChange={(_, data) => {
                      const harvestingOptions = data.checked ?
                        handleChecked(harvestingOption) :
                        handleUnchecked(harvestingOption, indexChecked)

                      setValue('maizeSilage.harvestingMethods', harvestingOptions)
                      if (watchHarvesting?.length > 0) {
                        clearErrors('maizeSilage.harvestingMethods')
                      }
                    }}
                  />
                  {displaySliderLabels && <HarvestSlider
                    primaryHarvestKey='maizeSilage.harvestingMethods[0].percent'
                    secondaryHarvestKey='maizeSilage.harvestingMethods[1].percent'
                  />}
                </Form.Field>
              })}
            </div>
          </HarvestingWrapper>
        )}
      />
      {isMaturity && <Controller
        control={control}
        name='maizeSilage.maturityLevelId'
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Reifegrad'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {maturityLevels.map(maturityLevelOption => {
                return <Checkbox
                  radio
                  key={maturityLevelOption.id}
                  label={maturityLevelOption.label}
                  checked={watchMaturity === maturityLevelOption.id}
                  onClick={() => {
                    setValue('maizeSilage.maturityLevelId', maturityLevelOption.id)
                    clearErrors('maizeSilage.maturityLevelId')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />}
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      {isGrain && isMaturity && <Controller
        control={control}
        name='maizeSilage.grainContent'
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Körneranteil'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {grainContentTypes.map(grainContentType => {
                return <Checkbox
                  radio
                  key={grainContentType.key}
                  label={grainContentType.label}
                  checked={watchGrainContent === grainContentType.key}
                  onClick={() => {
                    setValue('maizeSilage.grainContent', grainContentType.key)
                    clearErrors('maizeSilage.grainContent')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />}
      <Controller
        control={control}
        name='maizeSilage.averageFieldSize'
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Durchschnittliche Schlaggröße'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {fieldSizes.map(avgFieldSize => {
                return <Checkbox
                  radio
                  key={avgFieldSize.key}
                  label={avgFieldSize.label}
                  checked={watchStroke === avgFieldSize.key}
                  onClick={() => {
                    setValue('maizeSilage.averageFieldSize', avgFieldSize.key)
                    clearErrors('maizeSilage.averageFieldSize')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        noErrorMessage
        suffix=' % TM'
        typeInput={'controled'}
        label={'Trockenmassegehalt der Maissilage'}
        placeholder={'in % TM'}
        name={'maizeSilage.dryMatter'}
      />
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        suffix=' dt FM/ ha'
        noErrorMessage
        typeInput={'controled'}
        label={'Frischmasseertrag Silomais'}
        placeholder={'in dt FM/ ha'}
        name={'maizeSilage.freshMassYield'}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        noErrorMessage
        suffix=' MJ NEL/kg TM'
        typeInput={'controled'}
        label={'Energiegehalt der Silage'}
        placeholder={'in MJ NEL/kg TM'}
        name={'maizeSilage.energy'}
      />
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        suffix=' g XP/kg TM'
        noErrorMessage
        typeInput={'controled'}
        label={'Rohproteingehalt der Silage'}
        placeholder={'in g XP/kg TM'}
        name={'maizeSilage.rawProtein'}
      />
    </StyledGroup>

  </SilageStepLayout>
}
export default FirstCornStep